import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import AdminService from '../../services/AdminService';

const STATUS_COLORS = { active: 'success', inactive: 'default' };
const TYPE_COLORS = { admin: 'error', user: 'primary', developer: 'warning' };

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', mobile: '',
  type: 'user', status: 'active', password: '',
};

export default function AccountsView() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  function showSnack(message, severity = 'success') {
    setSnackbar({ open: true, message, severity });
  }

  async function fetchAccounts() {
    try {
      const data = await AdminService.getAllAccounts();
      setAccounts(data || []);
    } catch {
      showSnack('Failed to load accounts', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchAccounts(); }, []);

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(account) {
    setEditTarget(account);
    setForm({
      first_name: account.first_name || '',
      last_name: account.last_name || '',
      email: account.email || '',
      mobile: account.mobile || '',
      type: account.type || 'user',
      status: account.status || 'active',
      password: '',
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    try {
      if (editTarget) {
        await AdminService.updateAccount(editTarget.id, {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          mobile: form.mobile,
          status: form.status,
        });
        showSnack('Account updated');
      } else {
        await AdminService.createAccount(form);
        showSnack('Account created');
      }
      setDialogOpen(false);
      fetchAccounts();
    } catch {
      showSnack('Failed to save account', 'error');
    }
  }

  async function handleDeactivate() {
    try {
      await AdminService.deactivateAccount(deleteId);
      showSnack('Account deactivated');
      setDeleteId(null);
      fetchAccounts();
    } catch {
      showSnack('Failed to deactivate account', 'error');
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Accounts</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Account
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center">Loading…</TableCell></TableRow>
            ) : accounts.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No accounts found</TableCell></TableRow>
            ) : accounts.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell>{a.first_name} {a.last_name}</TableCell>
                <TableCell>{a.email || '—'}</TableCell>
                <TableCell>{a.mobile || '—'}</TableCell>
                <TableCell>
                  <Chip label={a.type} color={TYPE_COLORS[a.type] || 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={a.status} color={STATUS_COLORS[a.status] || 'default'} size="small" />
                </TableCell>
                <TableCell>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteId(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editTarget ? 'Edit Account' : 'Create Account'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="First Name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} fullWidth required />
            <TextField label="Last Name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} fullWidth />
          </Box>
          <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />
          <TextField label="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} fullWidth />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="developer">Developer</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {!editTarget && (
            <TextField label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} fullWidth />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.first_name}>
            {editTarget ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirm Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Deactivate Account</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to deactivate this account?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeactivate}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
